import { ApiHolder } from '@backstage/core-plugin-api';
// todo cross-plugin dependency - these should be in a shared package
// using relative imports as temporary workaround
// eslint-disable-next-line @backstage/no-relative-monorepo-imports
import { ragAiApiRef } from '../../../rag-ai/src/api';
// eslint-disable-next-line @backstage/no-relative-monorepo-imports
import type { ResponseEmbedding } from '../../../rag-ai/src/types';
import type {
  ConversationAssistant,
  Message,
  Conversation,
  ChatCompletionResponse,
  ChatAgentRequest,
  ChatAgentResponse,
} from './MgaApi';
import { LocalConversationStorage } from './LocalConversationStorage';
import {
  CHAT_COMPLETION_OBJECT,
  LOCAL_RAG_SOURCE,
  MessageRole,
  STORAGE_KEYS,
} from './constants';

export const RAG_ASSISTANT_ID = 'rag-assistant-backstage';

export const RAG_ASSISTANT_INFO: ConversationAssistant = {
  id: RAG_ASSISTANT_ID,
  name: 'AI Assistant',
  description:
    'AI-powered assistant that searches and answers questions about your software catalog, services, APIs, components, and technical documentation.',
  logoUrl: undefined,
  model: 'Backstage RAG',
  topic: 'Documentation',
  instructions:
    'Search and answer questions about the software catalog and documentation.',
};

export interface RagMessage {
  id: string;
  role: typeof MessageRole.USER | typeof MessageRole.ASSISTANT;
  content: string;
  embeddings?: ResponseEmbedding[];
  timestamp: string;
  cost?: number;
}

export interface RagConversation {
  id: string;
  messages: RagMessage[];
  title: string;
  source: string;
  timestamp: string;
}

export class RagAssistantAdapter {
  readonly conversationPrefix = 'rag';
  readonly assistantInfo = RAG_ASSISTANT_INFO;

  private apiHolder: ApiHolder;
  private conversationStorage = new LocalConversationStorage<RagConversation>({
    conversation: STORAGE_KEYS.ragConversation,
    conversationList: STORAGE_KEYS.RAG_CONVERSATION_LIST,
  });

  constructor(apiHolder: ApiHolder) {
    this.apiHolder = apiHolder;
  }

  private getRagApi() {
    return this.apiHolder.get(ragAiApiRef);
  }

  getConversation(conversationId: string): RagConversation | null {
    return this.conversationStorage.get(conversationId);
  }

  async sendMessage(
    request: ChatAgentRequest,
    options?: {
      onStream?: (response: ChatCompletionResponse) => void;
      ephemeral?: boolean;
    },
  ): Promise<ChatAgentResponse> {
    const ragApi = this.getRagApi();

    if (!ragApi) {
      throw new Error('RAG AI API not available');
    }

    // Extract the latest user message
    const userMessage = request.messages[request.messages.length - 1];
    if (!userMessage || userMessage.role !== MessageRole.USER) {
      throw new Error('No user message found in request');
    }

    const conversationId = request.conversation_id || `rag-${Date.now()}`;
    const source = LOCAL_RAG_SOURCE;

    // Get or create conversation
    let conversation = this.getConversation(conversationId);
    if (!conversation) {
      conversation = {
        id: conversationId,
        messages: [],
        title: userMessage.content.substring(0, 50),
        source,
        timestamp: new Date().toISOString(),
      };
    }

    const userRagMessage: RagMessage = {
      id: `msg-${Date.now()}-user`,
      role: MessageRole.USER,
      content: userMessage.content,
      timestamp: new Date().toISOString(),
      cost: 0,
    };
    conversation.messages.push(userRagMessage);

    // Send only the raw user question to the RAG API.
    // Previous conversation context is NOT prepended because the same query
    // string is used for both vector similarity search AND the LLM prompt.
    // Prepending old Q&A pollutes the embedding lookup, causing inconsistent
    // results (the vector search matches on the old answer text instead of
    // the new question). The RAG backend's own prompt template already
    // provides sufficient context from the retrieved embeddings.
    const questionWithContext = userMessage.content;

    let assistantContent = '';
    let embeddings: ResponseEmbedding[] = [];
    const totalCost = 0.001;

    try {
      // Stream the response from RAG API with context
      for await (const chunk of ragApi.ask(questionWithContext, source)) {
        switch (chunk.event) {
          case 'response': {
            assistantContent += chunk.data;

            // Stream partial response if callback provided
            if (options?.onStream) {
              options.onStream({
                conversationId: conversationId,
                model: RAG_ASSISTANT_INFO.model || 'Backstage RAG',
                content: chunk.data,
              } as ChatCompletionResponse);
            }
            break;
          }
          case 'embeddings': {
            embeddings = JSON.parse(chunk.data);
            break;
          }
          case 'warning':
          case 'error': {
            assistantContent =
              chunk.data || 'An error occurred while processing your request.';
            break;
          }
          default:
            break;
        }
      }

      const assistantRagMessage: RagMessage = {
        id: `msg-${Date.now()}-assistant`,
        role: MessageRole.ASSISTANT,
        content: assistantContent,
        embeddings,
        timestamp: new Date().toISOString(),
        cost: totalCost,
      };
      conversation.messages.push(assistantRagMessage);
      conversation.timestamp = new Date().toISOString();

      this.conversationStorage.save(conversation, options);

      // Send final stream chunk if streaming
      if (options?.onStream) {
        options.onStream({
          conversationId: conversationId,
          model: RAG_ASSISTANT_INFO.model || 'Backstage RAG',
          content: '',
        } as ChatCompletionResponse);
      }

      // Return MGA-compatible response
      return {
        id: conversationId,
        parent_id: null,
        object: CHAT_COMPLETION_OBJECT,
        created: Date.now(),
        model: RAG_ASSISTANT_INFO.model || 'Backstage RAG',
        system_fingerprint: null,
        usage: {
          prompt_tokens: userMessage.content.length,
          completion_tokens: assistantContent.length,
          total_tokens: userMessage.content.length + assistantContent.length,
        },
        choices: [
          {
            message: {
              role: MessageRole.ASSISTANT,
              content: assistantContent,
            },
            finish_reason: 'stop',
            index: 0,
          },
        ],
        prompt_annotations: null,
        cost: totalCost,
        conversation_id: conversationId,
        metadata: [
          {
            cost: { total_cost: totalCost },
            choices: [],
          },
        ],
      };
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'An unknown error occurred';

      const errorRagMessage: RagMessage = {
        id: `msg-${Date.now()}-assistant`,
        role: MessageRole.ASSISTANT,
        content: `Error: ${errorMessage}`,
        timestamp: new Date().toISOString(),
        cost: 0,
      };
      conversation.messages.push(errorRagMessage);
      conversation.timestamp = new Date().toISOString();
      this.conversationStorage.save(conversation, options);

      throw error;
    }
  }

  convertToMgaConversation(ragConversation: RagConversation): Conversation {
    const messages: Message[] = ragConversation.messages.map(msg => ({
      id: msg.id,
      role: msg.role,
      content: msg.content,
      cost: msg.cost || 0,
      creation_datetime: msg.timestamp,
      conversation_id: ragConversation.id,
      parent_id: null,
      ...(msg.embeddings && { embeddings: msg.embeddings }),
    }));

    return {
      id: ragConversation.id,
      messages,
      assistant: {
        id: RAG_ASSISTANT_ID,
        name: RAG_ASSISTANT_INFO.name,
        logo_url: RAG_ASSISTANT_INFO.logoUrl || '',
        description: RAG_ASSISTANT_INFO.description || '',
        model: {
          model: RAG_ASSISTANT_INFO.model || '',
          name: RAG_ASSISTANT_INFO.model || '',
        },
        tools: [],
        files: [],
      },
      name: ragConversation.title,
      modification_date: ragConversation.timestamp,
      total_cost: messages.reduce((sum, msg) => sum + (msg.cost || 0), 0),
      tools: [],
      files: [],
    };
  }

  getAllConversations(): RagConversation[] {
    return this.conversationStorage.getAll();
  }

  deleteConversation(conversationId: string): void {
    this.conversationStorage.delete(conversationId);
  }

  updateConversationTitle(conversationId: string, title: string): void {
    const conversation = this.getConversation(conversationId);
    if (!conversation) {
      throw new Error(`RAG conversation ${conversationId} not found`);
    }

    conversation.title = title;
    conversation.timestamp = new Date().toISOString();
    this.conversationStorage.save(conversation);
  }
}
